import React from "react";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faInstagram,
  faYoutube,
  faLinkedinIn,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import styled from "styled-components";

// Import sponsor images
import gebauerStiftung from "../assets/sponsor/sponsor_gebauer-stiftung.png";
import kantonZuerichBildungsdirektion from "../assets/sponsor/sponsor_kanton-zuerich-bildungsdirektion.png";
import kgv from "../assets/sponsor/sponsor_kgv.png";
import zueriOberland from "../assets/sponsor/sponsor_zuerioberland.png";
import zoOnline from "../assets/sponsor/sponsor_zo_online.png";
import { Link } from "../utils/router";


import fbbzo from "../assets/sponsor/fbbzo.jpg";
import bwszo from "../assets/sponsor/bwszo.png";
import bgg from "../assets/sponsor/bgu-net.png";
import bgv from "../assets/sponsor/BGV_Logo_241x70.jpg";
import bsr from "../assets/sponsor/bsr.png";
import gewerblicheBerufsschuleWetzikon from "../assets/sponsor/gewerbliche-berufsschule-wetzikon_i480.jpg";
import bfsu from "../assets/sponsor/bfsu.svg";
import bgv_pfaeffikon from "../assets/sponsor/bgv_logo_mit_Turbenthal.avif";
import sponsorAvzo from "../assets/sponsor/sponsor_avzo.jpg";
import hostpoint from "../assets/sponsor/hostpoint-logo-blue.png";
// The sponsor strip is white, so white (negative) logos vanish on it. Hustech
// offers a dark-text version on hustech.ch; Mock and BGU (white "BG", blue "U")
// only exist in white, so they get a black background below instead of us
// recolouring their logo.
import mockschriften from "../assets/sponsor/mockschriften.png";
import ehm from "../assets/sponsor/ehm.jpg";
import Hustech from "../assets/sponsor/Hustech-Logo_transparent-scaled-768x191.webp";
import crealine from "../assets/sponsor/Logo-crealine-media-systems-schwarz.webp";
import tanjasTextCafe from "../assets/sponsor/tanjas-text-cafe.png";
import odan from "../assets/sponsor/odan.webp";
import stadtWetzikon from "../assets/sponsor/stadt-wetzikon.svg";
import drafted from "../assets/sponsor/drafted-logo-schwarz.svg";

import { signIn, signOut, useUser } from "../utils/auth";
import { faUser } from "@fortawesome/free-solid-svg-icons";

type SecondarySponsor = {
  imageSrc: string;
  sponsorUrl: string;
  alt: string;
  // For logos that only exist in a light version: fill behind the logo.
  background?: string;
};

const secondarySponsorImages: SecondarySponsor[] = [
  {
    imageSrc: fbbzo,
    sponsorUrl: "https://www.forum-berufsbildungzo.ch/",
    alt: "Forum Berufsbildung",
  },
  {
    imageSrc: bwszo,
    sponsorUrl: "https://www.bwszo.ch/",
    alt: "Berufswahlschule Zürcher Oberland",
  },
  {
    imageSrc: bgg,
    sponsorUrl: "https://www.bgu-net.ch/",
    alt: "BGV Uster",
    background: "#000",
  },
  {
    imageSrc: bgv,
    sponsorUrl: "https://www.bgv-hinwil.ch/",
    alt: "BGV Hinwil",
  },
  {
    imageSrc: bsr,
    sponsorUrl: "https://www.bsrueti.ch/",
    alt: "Berufsschule Rüti",
  },
  {
    imageSrc: gewerblicheBerufsschuleWetzikon,
    sponsorUrl: "https://www.gbwetzikon.ch/",
    alt: "Gewerbliche Berufsschule Wetzikon",
  },
  {
    imageSrc: bfsu,
    sponsorUrl: "https://www.bfsu.ch/",
    alt: "Bildungszentrum Uster",
  },
  {
    imageSrc: bgv_pfaeffikon,
    sponsorUrl: "https://www.bgv-pfaeffikon.ch/",
    alt: "BGV Pfäffikon ZH",
  },
  {
    imageSrc: sponsorAvzo,
    sponsorUrl: "https://avzo.ch/",
    alt: "AVZO",
  },
  {
    imageSrc: hostpoint,
    sponsorUrl: "https://www.hostpoint.ch/",
    alt: "Hostpoint",
  },
  {
    imageSrc: mockschriften,
    sponsorUrl: "https://www.mockschriften.ch/",
    alt: "Mock",
    background: "#000",
  },
  {
    imageSrc: ehm,
    sponsorUrl: "https://ehm.ch/",
    alt: "EHM",
  },
  {
    imageSrc: Hustech,
    sponsorUrl: "https://www.hustech.ch/",
    alt: "Hustech",
  },
  {
    imageSrc: crealine,
    sponsorUrl: "https://crealine.net/",
    alt: "Crealine",
  },
  {
    imageSrc: tanjasTextCafe,
    sponsorUrl: "https://www.tanjafrei.ch/",
    alt: "Tanjas Text-CaFé",
  },
  {
    imageSrc: odan,
    sponsorUrl: "https://www.odan.ch/",
    alt: "ODAN",
  },
  {
    imageSrc: stadtWetzikon,
    sponsorUrl: "https://www.wetzikon.ch/",
    alt: "Stadt Wetzikon",
  },
  {
    imageSrc: drafted,
    sponsorUrl: "https://drafted.ch/",
    alt: "drafted",
  },
];

const mainSponsors = [
  {
    imageSrc: gebauerStiftung,
    sponsorUrl: "http://www.gebauerstiftung.ch/",
    alt: "Sponsor Gebauer Stiftung",
  },
  {
    imageSrc: kantonZuerichBildungsdirektion,
    sponsorUrl: "http://www.zh.ch/berufsbildungsfonds",
    alt: "Sponsor Kanton Zürich Bildungsdirektion",
  },
  {
    imageSrc: kgv,
    sponsorUrl: "https://www.kgv.ch/",
    alt: "Sponsor KGV",
  },
  {
    imageSrc: zueriOberland,
    sponsorUrl: "https://www.zuerioberland-wirtschaft.ch/wirtschaftsregion/",
    alt: "Sponsor Zürioberland Wirtschaft",
  },
  {
    imageSrc: zoOnline,
    sponsorUrl: "http://zo-online.ch/",
    alt: "Sponsor ZO Online",
  },
];

const FooterWrapper = styled.footer`
  background-color: #212529;
  color: #fff;
  padding: 3rem 0 1rem 0;
`;

const FooterSection = styled.div`
  margin-bottom: 2rem;
`;

const FooterTitle = styled.h5`
  font-weight: bold;
  margin-bottom: 1rem;
  color: #fff;
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.span`
  span {
    color: #adb5bd;
    text-decoration: none;
    font-size: 0.9rem;
    &:hover {
      color: #fff;
    }
    padding-right: 1rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;

  a {
    color: #fff;
    font-size: 1.5rem;
    &:hover {
      color: #0d6efd;
    }
  }
`;

const BottomBar = styled.div`
  border-top: 1px solid #495057;
  padding-top: 1rem;
  margin-top: 2rem;
  font-size: 0.9rem;
`;

const SponsorsContainer = styled.div`
  background-color: white;
  border: 2px solid #0d6efd;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1rem;

  ${FooterTitle} {
    color: #212529;
  }
`;

const SponsorLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
`;

const SecondarySponsorsRow = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
`;

const SecondarySponsorLink = styled(SponsorLink)`
  height: 40px;
  margin: 0 10px;
`;

// The rotating strip is a window onto a ring: after the last logo comes the
// first again, one step at a time. It used to be a window onto the list, which
// stopped at the last logo and then jumped back to the start in one go.
const sponsorWindow = (start: number, count: number) =>
  Array.from(
    { length: Math.min(count, secondarySponsorImages.length) },
    (_, i) => secondarySponsorImages[(start + i) % secondarySponsorImages.length]
  );

const Footer = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sponsorsToShow, setSponsorsToShow] = useState(6); // Default to 6
  const preloadedImagesRef = useRef<HTMLImageElement[]>([]);

  const user = useUser();

  useEffect(() => {
    const updateSponsorsToShow = () => {
      const width = window.innerWidth;
      if (width < 320) {
        setSponsorsToShow(1);
      } else if (width < 480) {
        setSponsorsToShow(2);
      } else if (width < 640) {
        setSponsorsToShow(3);
      } else if (width < 800) {
        setSponsorsToShow(4);
      } else if (width < 960) {
        setSponsorsToShow(5);
      } else {
        setSponsorsToShow(6);
      }
    };

    updateSponsorsToShow(); // Initial check
    window.addEventListener("resize", updateSponsorsToShow); // Update on resize

    // Preload the next set of images
    preloadedImagesRef.current = sponsorWindow(
      currentIndex + 1,
      sponsorsToShow
    ).map((sponsor) => {
      const img = new Image();
      img.src = sponsor.imageSrc;
      return img;
    });

    const timer = setInterval(() => {
      setCurrentIndex(
        (prevIndex) => (prevIndex + 1) % secondarySponsorImages.length
      );
    }, 3000);

    return () => {
      clearInterval(timer);
      window.removeEventListener("resize", updateSponsorsToShow);
    };
  }, [currentIndex, sponsorsToShow]);

  const visibleSponsors = sponsorWindow(currentIndex, sponsorsToShow);

  return (
    <FooterWrapper>
      <div className="container">
        <div className="row">
          {/* Sponsors Section */}
          <div className="col-md-12">
            <FooterSection>
              <SponsorsContainer>
                <FooterTitle>Sponsoren</FooterTitle>
                <div className="d-flex flex-wrap gap-4 mb-3">
                  {mainSponsors.map((sponsor, index) => (
                    <SponsorLink
                      key={index}
                      href={sponsor.sponsorUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="my-3 align-self-center mx-2"
                    >
                      <img
                        src={sponsor.imageSrc}
                        alt={sponsor.alt}
                        width={150}
                        height={50}
                        style={{ objectFit: "contain" }}
                        loading="lazy"
                      />
                    </SponsorLink>
                  ))}
                </div>
                <SecondarySponsorsRow>
                  {/* Keyed by sponsor, so a logo that stays in view keeps
                      its element and only the incoming one is new. */}
                  {visibleSponsors.map((sponsor) => (
                    <SecondarySponsorLink
                      key={sponsor.sponsorUrl}
                      href={sponsor.sponsorUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={sponsor.imageSrc}
                        alt={sponsor.alt}
                        width={100}
                        height={40}
                        style={{
                          objectFit: "contain",
                          ...(sponsor.background && {
                            background: sponsor.background,
                            padding: 4,
                            borderRadius: 4,
                          }),
                        }}
                        loading="lazy"
                      />
                    </SecondarySponsorLink>
                  ))}
                </SecondarySponsorsRow>
              </SponsorsContainer>
            </FooterSection>
          </div>

          {/* Navigation Links */}
          <div className="col-md-5">
            <div className="row">
              <div className="col-sm-6">
                <FooterSection>
                  <FooterTitle>Besucher</FooterTitle>
                  <FooterList>
                    <li>
                      <FooterLink>
                        <Link href="/besucher/allgemeine-informationen">
                          Allgemeine Informationen
                        </Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/besucher/eltern">Eltern</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/besucher/jugendliche">Schüler</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/besucher/lehrpersonen">Lehrpersonen</Link>
                      </FooterLink>
                    </li>
                  </FooterList>
                </FooterSection>

                <FooterSection>
                  <FooterTitle>Lernende</FooterTitle>
                  <FooterList>
                    <li>
                      <FooterLink>
                        <Link href="/ausstellende/zeig-was-du-kannst">
                          Zeig was du kannst!
                        </Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/ausstellende/reglement">Reglement</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/ausstellende/anmeldung">Anmeldung</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/lehrbetriebe">
                          Lehrbetriebe
                        </Link>
                      </FooterLink>
                    </li>
                  </FooterList>
                </FooterSection>
              </div>

              <div className="col-sm-6">
                <FooterSection>
                  <FooterTitle>Über uns</FooterTitle>
                  <FooterList>
                    <li>
                      <FooterLink>
                        <Link href="/ueber-uns/das-ist-der-lwzo">
                          Das ist der LWZO
                        </Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/ueber-uns/galerie">Galerie</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/ueber-uns/wir-suchen">Wir suchen</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/ueber-uns/vereinsmaterial">
                          Vereinsmaterial
                        </Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/medien">Medien</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/ueber-uns/sponsoren">Sponsoren</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/ueber-uns/kontakt">Kontakt</Link>
                      </FooterLink>
                    </li>
                  </FooterList>
                </FooterSection>

                <FooterSection>
                  <FooterTitle>Projekte</FooterTitle>
                  <FooterList>
                    <li>
                      <FooterLink>
                        <Link href="/projekte/lageplan">Lageplan</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/projekte">Projekte</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/projekte/personen">Lernende</Link>
                      </FooterLink>
                    </li>
                    <li>
                      <FooterLink>
                        <Link href="/projekte/mix-ansicht">Mix-Ansicht</Link>
                      </FooterLink>
                    </li>
                  </FooterList>
                </FooterSection>
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="col-md-3">
            <FooterSection>
              <FooterTitle>Social Media</FooterTitle>
              <SocialLinks>
                <a
                  href="https://www.facebook.com/lehrlingswettbewerb"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faFacebookF} />
                </a>
                <a
                  href="https://www.instagram.com/lehrlingswettbewerb/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
                <a
                  href="https://www.youtube.com/@lehrlingswettbewerbzuri-ob7148"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faYoutube} />
                </a>
                <a
                  href="https://www.linkedin.com/company/79090786"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faLinkedinIn} />
                </a>
                <a
                  href="https://www.tiktok.com/@lehrlingswettbewerb"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faTiktok} />
                </a>
              </SocialLinks>
            </FooterSection>

            {/* New Newsletter Section */}
            <FooterSection>
              <FooterTitle>Newsletter</FooterTitle>
              <FooterList>
                <li>
                  <FooterLink>
                    <Link href="/newsletter">Jetzt anmelden</Link>
                  </FooterLink>
                </li>
              </FooterList>
            </FooterSection>
          </div>
        </div>

        {/* Bottom Bar */}
        <BottomBar className="d-flex justify-content-between">
          <div>
            <FooterLink>
              <Link href="/datenschutz">Datenschutz</Link>
            </FooterLink>
            <FooterLink>
              <Link href="/ueber-uns/kontakt">Kontakt</Link>
            </FooterLink>
            <FooterLink>
              <Link href="/sitemap">Sitemap</Link>
            </FooterLink>
            <FooterLink>
              {user === null ? (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    signIn();
                  }}
                  style={{ cursor: "pointer" }}
                >
                  Login
                </a>
              ) : (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    signOut();
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={faUser} /> {user.name}
                </a>
              )}
            </FooterLink>
          </div>
          <div>© {new Date().getFullYear()} LWZO. Alle Rechte vorbehalten.</div>
        </BottomBar>
      </div>
    </FooterWrapper>
  );
};

export default Footer;
